<template>
    <div class="component">
        <div v-if="simplified" class="simplified-title text-center mb-5">
            <h1 v-html="trans(`general.page.space_forms.presets.types.${form}_`)"></h1>
        </div>
        <div v-if="!simplified && showHeader" class="d-block d-lg-flex justify-content-between align-items-center">
            <div class="mb-1 title" v-html="trans(`general.page.space_forms.presets.types.${form}_`)"></div>

            <basic-button
                cls="mb-1 px-0"
                icon="fi-rr-copy"
                @click="copyLinkToClipboard"
                :text="trans('general.defaults.copy_link')"/>
        </div>

        <div v-for="(email, index) in emails" :key="index" class="row mb-2">
            <div :class="['col-10 mb-2 pe-1 pe-lg-0', scope === 'employee' ? 'col-lg-5' : '']">
                <basic-input
                    type="email"
                    :label="trans('general.forms.invite.email.' + scope)"
                    :placeholder="trans('general.forms.invite.email.' + scope)"
                    v-model="emails[index]"
                    v-on:reset-error="errorEmail(emails[index], false)"
                    :error="errorIndexes.includes(index) ? 'error message' : null"
                    :show-error-message="false"
                    cls="w-100"/>
            </div>
            <div :class="['col-2 order-1 order-lg-3 mb-2 text-end ps-0 ps-lg-2',
            scope === 'employee' ? 'col-lg-1' : 'col-lg-2']">
                <basic-button
                    :disabled="emails.length <= 1"
                    cls="btn-secondary px-3 btn-fluid"
                    icon="fi fi-rr-cross-small"
                    @click="removeEmail(email)"/>
            </div>
            <div v-if="scope === 'employee'" :class="['order-2 order-lg-1 mb-2',
            (emailIndexData[index]?.role[0].id !== 'teacher')  ? 'col-12 col-lg-6 pe-lg-0' : 'col-6 col-lg-3 pe-0']">
                <k-select
                    :key="index"
                    :label="trans('general.forms.invite.select_role')"
                    :placeholder="trans('general.forms.invite.select_role')"
                    :allow-search="false"
                    :show-selected-options="false"
                    :required="true"
                    :options="roles"
                    :pre-selected="preSelectedRole"
                    auto-close="true"
                    v-on:change="setEmailIndexData(index, 'role', $event)"
                    v-on:mounted="mountedRoleSelect(index)"
                />
            </div>
            <div v-if="scope === 'employee'" :class="['col-6 order-3 order-lg-2 mb-2',
            (emailIndexData[index]?.role[0].id !== 'teacher')  ? 'd-none' : 'col-lg-3 pe-lg-0']">
                <k-select
                    v-if="emailIndexData[index]?.role[0]?.id === 'teacher'"
                    :label="trans('general.forms.invite.select_groups')"
                    :placeholder="trans('general.forms.invite.select_groups')"
                    :options="groups"
                    :multiple="true"
                    :server-side-search="true"
                    v-on:search="searchGroups"
                    v-on:change="setEmailIndexData(index, 'groups', $event)"
                />
            </div>
        </div>

        <basic-button
            v-if="emailsLeft > 0"
            cls="px-0"
            icon="fi-rr-plus btn-sm"
            :text="trans('general.defaults.add_more')"
            @click="addEmail()"/>

        <invited-emails v-if="simplified && scopeReady" :key="scope" :scope="scope" v-on:invitedEmailsList="invitedEmailsList"/>

        <div class="d-flex justify-content-end">
            <basic-button
                v-if="!simplified"
                cls="btn-primary"
                icon="fi-rr-paper-plane"
                icon-position="right"
                :text="trans('general.forms.send_invitations')"
                @click="sendEmails"
                :disabled="sending || emails.length < 1"
                :loading="sending"
            />
        </div>

        <setup-footer
            v-if="showFooter"
            ref="setupFooter"
            :step="step"
            :next-disabled="!showNextStep"
            @prevStep="$emit('prevStep')"
            @nextStep="onNextStep()"/>
    </div>
</template>

<script>
import BasicButton from "../../../theme/atoms/BasicButton.vue";
import VueTagsInput from "@johmun/vue-tags-input";
import axios from "axios";
import {SPACE_SETUP} from "../../../services/Defaults";
import SetupFooter from "../molecules/SetupFooter.vue";
import FormSubmissions from "../../../modules/SpaceForms/organisms/FormSubmissions.vue";
import NoContentPlaceholder from "../../../theme/organisms/NoContentPlaceholder.vue";
import BasicInput from "../../../theme/atoms/BasicInput.vue";
import KSelect from "../../../theme/molecules/KSelect.vue";
import {inviteRoles} from "../../../services/RoleService";
import InvitedEmails from "../molecules/InvitedEmails.vue";

export default {
    name: "Invite",
    props: {
        props: Object,
        showHeader: {
            type: Boolean,
            default: true
        },
        simplified: {
            type: Boolean,
            default: false
        },
        step: {
            type: Number
        },
    },
    components: {
        InvitedEmails,
        KSelect,
        BasicInput,
        FormSubmissions,
        SetupFooter,
        BasicButton,
        VueTagsInput,
        NoContentPlaceholder
    },
    data() {
        return {
            formData: true,
            scope: 'employee',
            selected: '',
            invalidTags: 0,
            emails: [''],
            errorIndexes: [],
            emailsLeft: SPACE_SETUP.INVITE_BY_EMAILS_LIMIT - 1,
            formId: null,
            formLink: '',
            form: null,
            sending: false,
            nextStep: true,
            showNextStep: false,
            emailIndexData: [],
            roles: [],
            preSelectedRole: [{
                id: 'teacher',
                label: trans('general.module.role.teacher'),
            }],
            groups: [],
            showFooter: false,
            scopeReady: false,
        }
    },
    methods: {
        onNextStep() {
            this.sendEmails();
        },
        configure() {
            if (!this.props) {
                return null;
            }

            Object.entries(this.props).forEach(([key, value]) => {
                this[key] = value
            });

            this.scopeReady = true;

            if(this.scope === 'employee') {
                this.roles = inviteRoles.map(role => {
                    return {
                        id: role,
                        label: trans('general.module.role.' + role),
                    }
                });

                this.searchGroups();
            }

            this.generateFormLink();
        },
        addEmail() {
            const emailLimit = SPACE_SETUP.INVITE_BY_EMAILS_LIMIT;
            if (this.emails.length >= emailLimit) return;
            const invalidEmail = this.emails.some(email => !this.isValidEmail(email));

            if (invalidEmail) {
                this.$notify({
                    type: 'error',
                    text: trans('general.setup.add_error')
                });
                return;
            }

            this.emails.push('');
            this.emailsLeft -= 1;
        },
        removeEmail(email) {
            const index = this.emails.indexOf(email);
            if (index !== -1) {
                this.emails.splice(index, 1);
                this.emailsLeft += 1;
            }
        },
        errorEmail(email, error = true) {
            const index = this.emails.indexOf(email);

            if (error) {
                if(!this.errorIndexes.includes(index)) {
                    this.errorIndexes.push(index);
                }
            } else {
                this.errorIndexes = this.errorIndexes.filter(item => item !== index);
            }
        },
        generateFormLink() {
            axios.get(`/space/forms/get-form/${this.form}`)
                .then(response => {
                    this.formData = response.data.data;

                    if (!this.formData) {
                        this.stepDone();
                        if(this.nextStep) {
                            this.showNextStep = true;
                        }
                    } else {
                        this.formId = this.formData.id;
                        this.formLink = `${appConfig.appUrl}/space/forms/${this.formData.id}/view`;
                    }
                })
                .catch(error => {
                    this.$notify({type: 'error', text: error.response.data.message});
                })
        },
        async copyLinkToClipboard() {
            if (!this.formData) {
                if (!await this.installForm()) {
                    return;
                }
            }

            try {
                await navigator.clipboard.writeText(this.formLink);
                this.$notify({type: 'success', text: trans('general.forms.copied_to_clipboard')});
                this.stepDone();
            } catch (err) {
                this.$notify({type: 'error', text: trans('general.forms.copy_failed')});
            }
        },
        isValidEmail(email) {
            const pattern = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            return pattern.test(email);
        },
        async sendEmails() {
            this.sending = true;

            if (this.formData) {
                return this.submitEmails();
            } else {
                this.$emit('nextStep');
            }

            if(await this.installForm()) {
                return this.submitEmails();
            }
        },
        async installForm() {
            try {
                const spaceFormPreset = await this.getSpaceFormPreset();

                if (spaceFormPreset) {
                    const spaceForm = await this.createSpaceForm(spaceFormPreset.id);

                    if (spaceForm) {
                        this.formData = spaceForm;
                        this.generateFormLink();
                        return true;
                    }
                }

                this.$notify({ type: 'error', text: trans('general.space_forms.no_form') });
            } catch (error) {
                this.$notify({ type: 'error', text: trans('general.forms.sending_error') });
            } finally {
                this.sending = false;
            }

            return false;
        },
        getSpaceFormPreset() {
            return axios.get(`/space/forms/get-space-form-preset/${this.form}`)
                .then(response => {
                    const data = response.data.data;
                    if (data) {
                        return data;
                    } else {
                        return null;
                    }
                });
        },
        createSpaceForm(formPresetId) {
            return axios.post(`/space/forms/new`, {
                name: trans(`general.page.space_forms.presets.types.${this.form}`),
                form_preset_id: formPresetId,
                description: ''
            }).then(response => {
                    const data = response.data.data;
                    if (data) {
                        return data;
                    } else {
                        return null;
                    }
                });
        },
        submitEmails() {
            let invalidEmails = [];
            let validEmails = [];
            let invitationsArray = []
            this.emails.forEach(email => {
                if (this.isValidEmail(email)) {
                    validEmails.push(email);

                    let index = this.emails.indexOf(email);
                    invitationsArray.push({
                        email: email,
                        role: this.scope === 'employee' ?
                            (this.emailIndexData[index]?.role[0]?.id ?? this.preSelectedRole[0]?.id) : 'parent',
                        groups: JSON.stringify(this.emailIndexData[index]?.groups ? this.emailIndexData[index].groups.map(group => group.id) : [])
                    });

                } else {
                    if(email !== '') {
                        invalidEmails.push(email);
                    }
                }
            });

            if (validEmails.length > 0) {
                let form = new FormData();
                form.append('form', this.form);
                form.append('invitations', JSON.stringify(invitationsArray));
                this.sendFormData(form);
                validEmails.forEach(email => {
                    this.removeEmail(email);
                });
                this.$emit('stepDone');
                this.$emit('emails-sent', validEmails);
            }

            if (invalidEmails.length > 0) {
                invalidEmails.forEach(email => {this.errorEmail(email)});
                const result = invalidEmails.map(email => `"<b>${email}</b>"`).join(', ');
                this.$notify({
                    type: 'error',
                    text: `${result} ${trans('general.setup.' +
                            (invalidEmails.length > 1 ? 'invalid_emails' : 'invalid_email'))}`
                });
            }

            this.$emit('nextStep');
            this.sending = false;
        },
        sendFormData(form) {
            axios.post(`/space/forms/send-form`, form)
                .then(response => {
                    const data = response.data.data;

                    if (data.status === 'success') {
                        this.$notify({type: 'success', text: data.message});
                        this.stepDone();
                        this.$emit('emails-sent', this.emails);
                        // this.resetOptions();
                    } else {
                        this.$notify({type: 'error', text: data.message});
                    }

                    this.sending = false;
                })
                .catch(error => {
                    this.$notify({type: 'error', text: error.response.data.message});
                    this.sending = false;
                })
        },
        resetOptions() {
            this.emails = [''];
            this.emailIndexData = [];
            this.emailsLeft = SPACE_SETUP.INVITE_BY_EMAILS_LIMIT - 1;
            this.errorIndexes = [];
        },
        stepDone() {
            this.$emit('stepDone');
            if(this.nextStep) {
                this.showNextStep = true;
            }
        },
        setFooterBtnText(state) {
            this.$refs.setupFooter.setButtonText(state);
        },
        setEmailIndexData(index, context, $event) {
            if (!this.emailIndexData[index]) {
                this.$set(this.emailIndexData, index, {});
                this.$set(this.emailIndexData[index], context, $event);
            } else {
                if (!this.emailIndexData[index][context]) {
                    this.$set(this.emailIndexData[index], context, $event);
                } else {
                    this.emailIndexData[index][context] = $event;
                }
            }
        },
        searchGroups(query = '') {
            axios.get(`/classes?query=${query}`)
                .then(response => {
                    this.groups = response.data.data.data.map(group => {
                        return {
                            id: group.id,
                            label: group.name,
                        }
                    });
                })
        },
        mountedRoleSelect(index) {
            if (this.preSelectedRole) {
                this.setEmailIndexData(index, 'role', this.preSelectedRole);
            }
        },
        invitedEmailsList(invitedEmailsList) {
            if (invitedEmailsList.length > 0) {
                this.$emit('stepDone');
            }

            this.showNextStep = true;
        }
    },
    mounted() {
        this.configure();
    }
}
</script>

<style scoped>
.share-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.seperator {
    position: relative;
    border-top: 1px solid #E3E3E3;
    margin: 50px 0;
}

.seperator span {
    border-radius: 100px;
    background: #ffffff;
    position: absolute;
    border: 1px solid #ddd;
    width: 40px;
    height: 40px;
    top: -20px;
    left: calc(50% - 10px);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    color: #7b7b7b;
    text-transform: uppercase;
    font-size: 11px;
}

.error-class input {
    border-color: #B20000
}
.counter {
    margin-top: -20px;
}

.viber path {
    fill: #3E70FF;
}
</style>
